import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Outlet, useParams } from 'react-router-dom'
import { useLocalStorage } from 'react-use'

import { getWindow } from '@ztechapps/common.utils'
import { useHistory } from '@ztechapps/common.hook'

import { ShowIf } from '../ShowIf'
import { Icon } from '../Icon'
import { useDragResize } from '../drag-resize'
import { TooltipPopOver } from '../TooltipPopOver'

import styles from './RouteOffCanvas.module.scss'

type RouteOffCanvasProps = {
  children: React.ReactNode
  listRoutePath: string
  defaultWidth?: string
  widthStorageKey?: string
}

export const RouteOffCanvas = ({
  children,
  listRoutePath,
  defaultWidth = '90%',
  widthStorageKey,
}: RouteOffCanvasProps) => {
  const history = useHistory()
  const { _id } = useParams()
  const [show, setShow] = useState(!!_id)
  const storageKey = widthStorageKey ?? `${listRoutePath}-offcanvas-width`
  const [panelWidth, setPanelWidth] = useLocalStorage<string>(storageKey, defaultWidth)
  const layoutRef = useRef<HTMLDivElement>(null)
  const resizeOriginRef = useRef({ right: 0, width: 0 })

  const onStart = () => {
    // Measure-once-into-a-ref, same gesture as useColumResize (libs/webui/data-tools/src/DataTable/hooks/useColumResize.ts:20-32).
    const rect = layoutRef.current?.getBoundingClientRect()
    resizeOriginRef.current = { right: rect?.right ?? 0, width: rect?.width ?? 0 }
  }

  const onDrag = ({ clientX }: { clientX: number }) => {
    const { right, width } = resizeOriginRef.current
    const newWidth = right - clientX
    const widthPercent = (newWidth / width) * 100

    // Constrain between 10% and 95%
    const constrainedWidth = Math.min(Math.max(widthPercent, 10), 95)
    setPanelWidth(`${constrainedWidth}%`)
  }

  const { isResizing, onMouseDown } = useDragResize({ cursor: 'ew-resize', onStart, onDrag })

  useEffect(() => {
    setShow(!!_id)
  }, [_id])

  useEffect(() => {
    const doc = getWindow()?.document
    if (!doc) return

    if (show) {
      doc.body.style.overflow = 'hidden'
    } else {
      doc.body.style.overflow = ''
    }

    return () => {
      doc.body.style.overflow = ''
    }
  }, [show])

  const handleClose = useCallback(() => {
    history.push(listRoutePath)
    setShow(false)
  }, [history, listRoutePath])

  return (
    <div
      ref={layoutRef}
      className={`${styles.offcanvasLayout} d-flex w-100 flex-grow-1 ${show ? styles.offcanvasOpen : ''}`}
      style={{ '--offcanvas-width': panelWidth } as React.CSSProperties}
    >
      <div className={`${styles.offcanvasMainContent} d-flex flex-column w-100 overflow-x-auto bg-body text-body`}>
        {children}
      </div>

      <ShowIf condition={show}>
        <TooltipPopOver tooltipBody="Close">
          <button
            type="button"
            className={`${styles.closeButton} btn btn-primary btn-sm rounded-circle shadow d-flex align-items-center justify-content-center`}
            style={{ top: '0.5rem', zIndex: 11, width: '2rem', height: '2rem' }}
            onClick={handleClose}
            aria-label="Close"
          >
            <Icon name="times" />
          </button>
        </TooltipPopOver>
      </ShowIf>

      <div
        className={`${styles.offcanvasPanel} vh-100 d-flex flex-column bg-body text-body border-start shadow-lg`}
        style={{ zIndex: 10 }}
      >
        <TooltipPopOver tooltipBody="Drag to resize">
          <div className={`${styles.resizeHandle} ${isResizing ? styles.resizing : ''}`} onMouseDown={onMouseDown} />
        </TooltipPopOver>
        <div className={`${styles.panelContent} p-3 d-flex flex-column flex-grow-1 overflow-y-auto`}>
          <Outlet context={{ noTopMargin: true }} />
        </div>
      </div>
    </div>
  )
}
